<script>
import { mapStores } from "pinia";

import TgModal from "@/abc-dsk/components/Modal/TgModal.vue";
import TgSelectorButton from "@/abc-dsk/components/SelectorButton/TgSelectorButton.vue";
import TgSelectorButtonGroup from "@/abc-dsk/components/SelectorButton/TgSelectorButtonGroup.vue";
import { translateLanguageGroup } from "@/common/language-group-helpers";
import { addPackageAndLangParams } from "@/common/route-path-helpers";
import ImageResponsive from "@/components/ImageResponsive.vue";
import { useSearchStore, useSettingsStore } from "@/stores";

export default {
  name: "LanguageSelectorModal",
  components: {
    ImageResponsive,
    TgModal,
    TgSelectorButton,
    TgSelectorButtonGroup
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ["close"],
  computed: {
    ...mapStores(useSearchStore, useSettingsStore),

    prioritizedPackageGroups() {
      return this.settingsStore.prioritizedPackageGroups;
    },

    prioritizedLanguageGroups() {
      return this.settingsStore.prioritizedLanguageGroups;
    },

    activeLanguageGroup() {
      return this.settingsStore.activeLanguageGroup;
    },

    activePackageGroup() {
      return this.prioritizedPackageGroups.find((p) => p.isActive);
    },

    prioritizedPackageGroupIds() {
      return this.prioritizedPackageGroups.map((p) => p.id);
    }
  },
  methods: {
    async onLanguageButtonClick(language) {
      if (this.activeLanguageGroup !== language) {
        await Promise.all([
          this.settingsStore.setActiveLanguageGroup(language),
          this.searchStore.resetAdjustedSearchParameters(),
          this.searchStore.resetSearchParameters()
        ]);

        this.redirect();
      }

      this.$emit("close");
    },

    async onPackageChange(id) {
      const packageGroup = this.getPackageGroupById(id);
      if (packageGroup.isActive) {
        return;
      }

      await Promise.all([
        this.settingsStore.updateActivePackageGroup(packageGroup.id),
        this.settingsStore.setActiveLanguageGroup(undefined),
        this.searchStore.resetAdjustedSearchParameters(),
        this.searchStore.resetSearchParameters()
      ]);

      this.redirect();
    },

    redirect() {
      if (this.$router.currentRoute.value.name === "article") {
        this.$router.push({ name: "dictionary" });
      }

      if (
        this.$router.currentRoute.value.name === "workshop" ||
        this.$router.currentRoute.value.name.toString().startsWith("workshop.")
      ) {
        this.$router.push(addPackageAndLangParams(this.$route));
      }
    },

    getPackageGroupById(id) {
      return this.prioritizedPackageGroups.find((p) => p.id === id);
    },

    translateLanguageGroup: translateLanguageGroup
  }
};
</script>

<template>
  <TgModal
    dialog-class="language-selector-modal"
    body-class="language-selector-modal__body"
    header-class="language-selector-modal__header"
    footer-class="language-selector-modal__footer"
    :is-open="isOpen"
    :footer="prioritizedPackageGroups.length > 1 ? 'show' : 'hide'"
    :prevent-closing="!activeLanguageGroup"
    @close="$emit('close')"
  >
    <h3 class="language-selector-modal__headline">{{ $t("languageSelector.headline") }}</h3>
    <TgSelectorButtonGroup
      v-slot="{ state, handleBlur, handleFocusNext, handleFocusPrev, handleSelect }"
      class="language-selector-modal__buttons"
      :selected="activeLanguageGroup"
      :items="prioritizedLanguageGroups"
      @click-selected="$emit('close')"
      @input="onLanguageButtonClick($event.item)"
    >
      <TgSelectorButton
        v-for="(language, index) in prioritizedLanguageGroups"
        :key="language"
        :index="index"
        :tabbable="state.tabbableIndex === index"
        :selected="state.selectedIndex === index"
        size="lg"
        class="language-selector-modal__button"
        :aria-label="
          $t('languageSelector.languageButtonLabel', {
            language: translateLanguageGroup(language)
          })
        "
        @blur="handleBlur($event)"
        @select="handleSelect($event)"
        @focus-next="handleFocusNext($event)"
        @focus-prev="handleFocusPrev($event)"
      >
        <span
          class="language-selector-modal__button-inner-container language-selector-modal__button-flag-container"
        >
          <ImageResponsive
            class="language-selector-modal__button-flag"
            :src="`/assets/img/language-group-button-${language}@1x.png`"
            presentational
          />
        </span>
        <span class="language-selector-modal__button-inner-container">
          <span
            class="language-selector-modal__button-label language-selector-modal__button-label--prominent"
            >{{ translateLanguageGroup(language) }}</span
          >
          <span class="language-selector-modal__button-label">
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
            {{ $t(`languageGroups.${language}.original`) }}
          </span>
        </span>
      </TgSelectorButton>
    </TgSelectorButtonGroup>

    <template #modal-footer>
      <div v-if="prioritizedPackageGroups.length > 1" class="container fluid">
        <div class="row">
          <div class="col-xs-12 col-lg-10 offset-lg-1">
            <TgSelectorButtonGroup
              v-slot="{ state, handleBlur, handleFocusNext, handleFocusPrev, handleSelect }"
              class="language-selector-modal__package-buttons"
              :selected="activePackageGroup.id"
              :items="prioritizedPackageGroupIds"
              @input="onPackageChange($event.item)"
            >
              <TgSelectorButton
                v-for="(packageGroup, index) in prioritizedPackageGroups"
                :key="packageGroup.id"
                :index="index"
                :tabbable="state.tabbableIndex === index"
                :selected="state.selectedIndex === index"
                size="sm"
                :aria-label="
                  $t('languageSelector.packageButtonLabel', {
                    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
                    package: $t(`packageGroups.${packageGroup.id}`)
                  })
                "
                @blur="handleBlur($event)"
                @select="handleSelect($event)"
                @focus-next="handleFocusNext($event)"
                @focus-prev="handleFocusPrev($event)"
              >
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                {{ $t(`packageGroups.${packageGroup.id}`) }}
              </TgSelectorButton>
            </TgSelectorButtonGroup>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <p class="language-selector-modal__package-text">
              {{ $t("languageSelector.packageText") }}
            </p>
          </div>
        </div>
      </div>
    </template>
  </TgModal>
</template>
